<script setup lang="ts">
// i18n
import SpriteSelectorPopup from "@/components/character-generator/SpriteSelectorPopup.vue";
import RedotTitle from "@/components/mobile/common/titles/RedotTitle.vue";
import { HouseEventScene } from "@/features/dream-home/scenes/HouseEventScene";
import { useHouseScene } from "@/features/dream-home/store/houseScene";
import { useHouseSession } from "@/features/dream-home/store/houseSession";
import { useGlobalUser } from "@/stores/globalUser";
import {
  HouseEventEnding,
  MateHouseSessionEventParticipant,
} from "@/types/entity";
import { showLoadingToast } from "vant";
import { computed, ref } from "vue";
import { onBeforeRouteLeave } from "vue-router";
import EventListItem from "./EventListItem.vue";
import { useName } from "@/use/common";
import { fixNumber } from "@/components/Mission/MissionStat";
import { useGlobalModal } from "@/stores/globalModal";

const props = defineProps<{
  game: Phaser.Game;
}>();

const houseStore = useHouseSession();
const userStore = useGlobalUser();
const houseSceneStore = useHouseScene();
const modalStore = useGlobalModal();
const isAllEndingGenerated = computed(() => {
  if (!houseStore.eventList) return false;
  return houseStore.eventList.every((item) =>
    item.endingzz.every(
      (ending) => ending.completedHouseSessionEventzz.length > 0
    )
  );
});

const isGenerateAudio = ref(true);

const onConfirmSpriteSelect = async (selectedSpriteIds: number[]) => {
  const loading = showLoadingToast("");
  try {
    if (
      modalStore.shouldOpenPowerNotEnoughModal({
        spentPowerAmount: fixNumber(
          userStore.getPowerCostByModel(selectedSpriteIds.length * 2) +
            (isGenerateAudio.value ? selectedSpriteIds.length : 0)
        ),
        cb: () => {
          onConfirmSpriteSelect(selectedSpriteIds);
        },
      })
    )
      return;

    const endingId = houseStore.showEventEndingDetailPopup
      ? targetEnding.value?.id
      : undefined;
    await houseStore.createEvent(
      selectedSpriteIds,
      endingId,
      isGenerateAudio.value
    );
    await userStore.refresh();
  } finally {
    loading.close();
    if (houseStore.showEventEndingDetailPopup) {
      houseStore.showEventEndingDetailPopup = false;
    }
  }
};

const startEvent = async (sessionEventId?: number) => {
  if (!sessionEventId) {
    sessionEventId = houseStore.generatedButNotPlayedEvent!.id;
  }
  await houseSceneStore.startEvent(sessionEventId);
  props.game.scene.remove("House");
  props.game.scene.add("HouseEvent", HouseEventScene, true);
};

const targetEnding = ref<HouseEventEnding | null>(null);
const onViewEnding = async (ending: HouseEventEnding) => {
  targetEnding.value = ending;
  houseStore.showEventEndingDetailPopup = true;
};
const regenerateEnding = async () => {
  houseStore.showEventSpriteSelectorPopup = true;
};

onBeforeRouteLeave(() => {
  if (houseStore.showEventSpriteSelectorPopup) {
    houseStore.showEventSpriteSelectorPopup = false;
  }
});

const hasInvalidMate = (participantzz: MateHouseSessionEventParticipant[]) => {
  return participantzz.some((p) => !p.mate);
};
</script>
<template>
  <div class="h-full grid grid-rows-[auto_1fr] overflow-y-hidden">
    <div class="flex p-2 w-full">
      <div
        v-if="houseStore.hasGeneratingEvent"
        class="relative with-shadow flow-btn p-2 rounded text-[#AAA] bg-zinc-900 text-shadow-grey font-bold overflow-hidden italic border flex-1 flex justify-center"
      >
        事件准备中，预计等待30秒...
      </div>
      <button
        v-else-if="houseStore.generatedButNotPlayedEvent"
        class="relative flow-btn p-2 rounded bg-primary text-primary-content font-bold overflow-hidden border flex-1 justify-center"
        @click="startEvent()"
      >
        开始事件
      </button>
      <button
        v-else-if="!isAllEndingGenerated"
        class="relative flow-btn p-2 rounded bg-primary text-primary-content font-bold overflow-hidden border flex items-center flex-1 justify-center"
        @click="houseStore.showEventSpriteSelectorPopup = true"
      >
        <i class="iconify mdi--exclamation" />
        激活事件
      </button>
      <div
        v-else-if="isAllEndingGenerated"
        class="text-base-content/50 flex-1 text-center p-2"
      >
        事件解锁进度 100%
      </div>
    </div>
    <div class="h-full overflow-hidden">
      <RedotTitle title="历史事件" class="ml-4 text-sm" />
      <ThreadList
        :enable-pull-load="false"
        :onScrollLoad="houseStore.fetchNextEventList"
        class=""
      >
        <div class="flex flex-col gap-1 p-2">
          <EventListItem
            v-for="(item, index) in houseStore.eventList"
            :key="item.id"
            :event="item"
            :index="index"
            @view-ending="onViewEnding"
            @start-event="startEvent($event.id)"
          />
        </div>
      </ThreadList>
    </div>
    <SpriteSelectorPopup
      v-model:show="houseStore.showEventSpriteSelectorPopup"
      title="选择参与事件的角色"
      @on-confirm="onConfirmSpriteSelect"
    >
      <template #tip="{ newSelectedSpriteIds }">
        <div class="flex flex-col gap-1">
          <div class="flex items-center gap-1">
            <template
              v-if="
                userStore.getPowerCostByModel(newSelectedSpriteIds.length * 2) +
                  (isGenerateAudio ? newSelectedSpriteIds.length : 0) >
                0
              "
            >
              预计消耗:
              <PowerIcon class="relative -top-[1px]" />
              {{
                fixNumber(
                  userStore.getPowerCostByModel(
                    newSelectedSpriteIds.length * 2
                  ) + (isGenerateAudio ? newSelectedSpriteIds.length : 0)
                )
              }}
            </template>
            <div v-else class="ml-8" />
            <SelectModelBtn />
          </div>
        </div>
      </template>
      <template #tip2>
        <div class="px-2 flex justify-end">
          <div class="flex items-center gap-1">
            <input
              class="checkbox checkbox-xs checkbox-primary rounded"
              type="checkbox"
              v-model="isGenerateAudio"
            />
            同时生成语音
          </div>
        </div>
      </template>
    </SpriteSelectorPopup>
    <VanPopup
      class="h-[50%] w-full grid grid-rows-[auto_1fr] overflow-hidden"
      v-model:show="houseStore.showEventEndingDetailPopup"
      position="bottom"
    >
      <ThreadsNav
        show-back
        :show-center="false"
        :go-back="() => (houseStore.showEventEndingDetailPopup = false)"
      />
      <div class="h-full overflow-y-auto flex flex-col gap-2">
        <div class="flex items-center gap-2 justify-between pr-2">
          <RedotTitle :title="targetEnding!.title" size="md" class="ml-6" />
          <button class="btn btn-xs rounded" @click="regenerateEnding">
            重新演绎
          </button>
        </div>
        <div
          v-for="sessionEvent in targetEnding?.completedHouseSessionEventzz"
          :key="sessionEvent.id"
          class="flex flex-col gap-2 w-full p-2 overflow-hidden border-b first:border-t"
          :class="{
            'opacity-50': hasInvalidMate(sessionEvent.participantzz),
          }"
        >
          <div class="flex items-center gap-1 w-full overflow-x-scroll">
            <div
              v-for="p in sessionEvent.participantzz"
              :key="p.id"
              class="shrink-0 flex flex-col gap-1 items-center w-14 overflow-hidden"
            >
              <UserAvatar class="w-14" :mate="p.mate" />
              <div class="text-sm text-center line-clamp-1">
                {{ p.mate ? useName(p.mate.name) : "已删除" }}
              </div>
            </div>
          </div>
          <div class="flex justify-between items-center">
            <button
              v-if="!hasInvalidMate(sessionEvent.participantzz)"
              class="btn btn-xs rounded btn-primary"
              @click="startEvent(sessionEvent.id)"
            >
              播放
            </button>
            <div v-else class="text-sm text-red-500 flex items-center gap-1">
              <i class="iconify mdi--exclamation" />
              角色已删除，无法播放
            </div>
            <div
              v-if="sessionEvent.isMessageAudioNeeded"
              class="text-xs text-base-content/50"
            >
              支持语音
            </div>
          </div>
        </div>
      </div>
    </VanPopup>
  </div>
</template>

<style scoped>
.flow-btn.with-shadow {
  text-shadow: -1.3px -1.3px #000, -1.3px 1.3px #000, 1.3px 1.3px #000;
}

.flow-btn:before {
  animation: move-stripes 2s linear infinite;
  background: repeating-linear-gradient(
    60deg,
    hsla(0, 0%, 100%, 0.05),
    hsla(0, 0%, 100%, 0.05) 30px,
    transparent 0,
    transparent 50px
  );
  bottom: 0;
  content: "";
  height: 400px;
  left: 0;
  pointer-events: none;
  position: absolute;
  width: 150%;
}
@keyframes move-stripes {
  0% {
    transform: translate(0) translateY(0);
  }
  100% {
    transform: translate(-29px) translateY(50.23px);
  }
}
</style>
